import { useEffect, useMemo, useState, type ReactNode } from "react";
import { dailyReportTotals, formatUnitTotals, parsePhaseCodeText, phaseCodeText, type DailyReport } from "@centerline/core/viewer";
import { api, type ProjectSummary } from "./api";
import { Header } from "./Header";
import { href, navigate } from "./router";
import { canApprove, useMe } from "./session";
import { statusLabel } from "./status";

type Row = DailyReport & { signed: boolean };

export function DailyReports({ query: routeQuery = "", onSignOut, projectId, tick = 0 }: { query?: string; onSignOut?: () => void; projectId?: string; tick?: number }) {
  // Embedded in a project workspace: filters live in local state, scoped to that project.
  const [localQuery, setLocalQuery] = useState(projectId ? `projectId=${projectId}` : "");
  const query = projectId ? localQuery : routeQuery;
  const params = useMemo(() => new URLSearchParams(query), [query]);
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [reports, setReports] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const f = { projectId: params.get("projectId") ?? "", from: params.get("from") ?? "", to: params.get("to") ?? "", foreman: params.get("foreman") ?? "", status: params.get("status") ?? "" };
  const [foreman, setForeman] = useState(f.foreman);
  const me = useMe();
  const office = canApprove(me);
  const [awaiting, setAwaiting] = useState<Row[]>([]);

  const load = () => {
    if (office) api.dailyReports(new URLSearchParams({ status: "submitted", ...(projectId ? { projectId } : {}) }).toString()).then((r) => setAwaiting(r.reports)).catch(() => {});
    return api.dailyReports(query).then((r) => { setReports(r.reports); setPicked(new Set()); }).catch((e) => setError(e.message));
  };
  useEffect(() => { api.projects().then((r) => setProjects(r.projects)).catch(() => {}); }, []);
  useEffect(() => { void load(); setForeman(f.foreman); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, tick]);

  const setFilter = (k: string, v: string) => {
    const p = new URLSearchParams(query);
    if (v) p.set(k, v); else p.delete(k);
    if (projectId) { p.set("projectId", projectId); setLocalQuery(p.toString()); } else navigate({ name: "reports", query: p.toString() }, true);
  };
  const name = (id: string) => projects.find((p) => p.id === id)?.name ?? "…";
  const exportUrl = (ext: string) => {
    const p = new URLSearchParams(query);
    if (picked.size) p.set("ids", [...picked].join(","));
    return `/api/daily-reports/export.${ext}?${p}`;
  };
  const approvePicked = async () => {
    setBusy(true);
    const errs: string[] = [];
    for (const r of reports ?? []) {
      if (!picked.has(r.id) || r.status !== "submitted") continue;
      try { await api.updateDailyReport(r.id, { version: r.version, status: "approved" }); } catch (e) { errs.push(`${r.date} ${r.foreman}: ${(e as Error).message}`); }
    }
    setBusy(false);
    setError(errs.length ? errs.join(" · ") : null);
    await load();
  };
  const totals = useMemo(() => {
    const t = { crewHours: 0, rainHours: 0, production: {} as Record<string, number> };
    for (const r of reports ?? []) {
      const x = dailyReportTotals(r);
      t.crewHours += x.crewHours; t.rainHours += x.rainHours;
      for (const [u, q] of Object.entries(x.production)) t.production[u] = Math.round(((t.production[u] ?? 0) + q) * 100) / 100;
    }
    return t;
  }, [reports]);
  const pickable = reports ?? [];
  const [phaseEdit, setPhaseEdit] = useState(false);

  return (
    <Wrap embedded={!!projectId} onSignOut={onSignOut}>
        <div className="row between wrap">
          <h2>{projectId ? "Dailies" : "Crew daily reports"}</h2>
          <a className="button primary" href={href({ name: "newReport", projectId: f.projectId || undefined })}>+ New daily report</a>
        </div>
        {office && awaiting.length > 0 && <section className="card awaiting" aria-label="Awaiting approval">
          <h3>Awaiting approval ({awaiting.length})</h3>
          <ul>{awaiting.map((r) => { const t = dailyReportTotals(r); return (
            <li key={r.id}><a href={href({ name: "report", id: r.id })}><span><strong>{r.date}</strong> · {r.foreman || "—"}{projectId ? "" : ` · ${name(r.projectId)}`}</span><span className="muted small">{formatUnitTotals(t.production) || `${t.crewHours} crew hrs`}</span></a></li>); })}</ul>
        </section>}
        <div className="dr-filters card">
          {!projectId && <label>Project<select value={f.projectId} onChange={(e) => setFilter("projectId", e.target.value)}><option value="">All projects</option>{projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></label>}
          <label>From<input type="date" value={f.from} onChange={(e) => setFilter("from", e.target.value)} /></label>
          <label>To<input type="date" value={f.to} onChange={(e) => setFilter("to", e.target.value)} /></label>
          <label>Foreman<input value={foreman} placeholder="Any" onChange={(e) => setForeman(e.target.value)} onBlur={() => setFilter("foreman", foreman.trim())} onKeyDown={(e) => { if (e.key === "Enter") setFilter("foreman", foreman.trim()); }} /></label>
          <label>Status<select value={f.status} onChange={(e) => setFilter("status", e.target.value)}><option value="">Any</option><option value="draft">Draft</option><option value="submitted">Awaiting approval</option><option value="approved">Approved</option></select></label>
          {query && query !== `projectId=${projectId}` && <button className="link" onClick={() => projectId ? setLocalQuery(`projectId=${projectId}`) : navigate({ name: "reports", query: "" }, true)}>Clear</button>}
        </div>
        <div className="row wrap dr-toolbar">
          <span className="muted">{reports ? `${reports.length} report${reports.length === 1 ? "" : "s"}` : "Loading…"}{picked.size ? ` · ${picked.size} selected` : ""}</span>
          <span className="spacer" />
          {office && <button disabled={!picked.size || busy} onClick={approvePicked}>Approve selected</button>}
          <a className="button" href={exportUrl("pdf")} target="_blank" rel="noreferrer">Print / PDF</a>
          <a className="button" href={exportUrl("csv")}>CSV</a>
          <a className="button" href={exportUrl("xlsx")}>Excel</a>
          {office && <button onClick={() => setPhaseEdit(true)}>Phase codes</button>}
        </div>
        {phaseEdit && <PhaseCodeEditor onClose={() => setPhaseEdit(false)} />}
        {error && <div className="error">{error}</div>}
        {reports?.length === 0 && <div className="empty">No daily reports match. Foremen start one from “+ New daily report” on their phone.</div>}
        {!!reports?.length && <div className="table-wrap card"><table className="dr-table">
          <thead><tr>
            <th><input type="checkbox" aria-label="Select all" checked={picked.size === pickable.length && pickable.length > 0} onChange={(e) => setPicked(e.target.checked ? new Set(pickable.map((r) => r.id)) : new Set())} /></th>
            <th>Date</th><th>Project</th><th>Foreman</th><th>Status</th><th className="num">Crew hrs</th><th className="num">Rain hrs</th><th>Production</th><th className="num">Tickets</th><th className="num">Photos</th><th>Weather</th>
          </tr></thead>
          <tbody>{reports.map((r) => { const t = dailyReportTotals(r); return (
            <tr key={r.id} onClick={(e) => { if ((e.target as HTMLElement).tagName !== "INPUT") navigate({ name: "report", id: r.id }); }}>
              <td><input type="checkbox" checked={picked.has(r.id)} onChange={(e) => { const s = new Set(picked); if (e.target.checked) s.add(r.id); else s.delete(r.id); setPicked(s); }} /></td>
              <td className="nowrap">{r.date}{r.shift === "night" ? " 🌙" : ""}</td>
              <td>{name(r.projectId)}</td>
              <td>{r.foreman || <span className="muted">—</span>}</td>
              <td><span className={`status-chip ${r.returnedAt && r.status === "draft" ? "returned" : r.status}`}>{statusLabel(r)}</span>{r.safety.incident && <span className="status-chip incident" title="Incident reported">!</span>}</td>
              <td className="num">{t.crewHours}</td>
              <td className="num">{t.rainHours || ""}</td>
              <td>{formatUnitTotals(t.production)}</td>
              <td className="num">{t.ticketCount || ""}</td>
              <td className="num">{r.photos.length || ""}</td>
              <td className="muted small">{r.weather.summary}{r.weather.highF !== null ? ` · ${r.weather.highF}°` : ""}</td>
            </tr>); })}</tbody>
          <tfoot><tr><td /><td colSpan={4}>Totals</td><td className="num">{Math.round(totals.crewHours * 100) / 100}</td><td className="num">{Math.round(totals.rainHours * 100) / 100 || ""}</td><td>{formatUnitTotals(totals.production)}</td><td colSpan={3} /></tr></tfoot>
        </table></div>}
    </Wrap>
  );
}

function Wrap({ embedded, onSignOut, children }: { embedded: boolean; onSignOut?: () => void; children: ReactNode }) {
  if (embedded) return <div className="dr-office-list">{children}</div>;
  return <div className="page"><Header onSignOut={onSignOut}><span>Daily Reports</span></Header><main className="content dr-office-list">{children}</main></div>;
}

/** Office editor for the Watson phase list foremen pick from: one "code, name, group" per line. */
function PhaseCodeEditor({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState<string | null>(null);
  const [custom, setCustom] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  useEffect(() => { api.phaseCodes().then((r) => { setText(phaseCodeText(r.codes)); setCustom(r.custom); }).catch((e) => setMsg({ kind: "error", text: e.message })); }, []);
  const save = async (reset = false) => {
    try {
      const r = reset ? await api.savePhaseCodes({ reset: true }) : await api.savePhaseCodes({ codes: parsePhaseCodeText(text ?? "") });
      setText(phaseCodeText(r.codes)); setCustom(r.custom); setMsg({ kind: "ok", text: `Saved ${r.codes.length} phase codes. Foremen see them next time they open a report.` });
    } catch (e) { setMsg({ kind: "error", text: (e as Error).message }); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card phase-editor" role="dialog" aria-label="Phase codes" onClick={(e) => e.stopPropagation()} style={{ width: "min(720px, 96vw)" }}>
        <h3>Watson phase codes</h3>
        <p className="muted small">One per line: <code>code, name, group</code>. Foremen pick these on crew, work, ticket and sub lines. Cost type is added by line: crew/work .1, materials .2, subs .4.{custom ? "" : " (Showing the built-in list from the Phase Code Reference sheet.)"}</p>
        {text === null ? <p className="muted">Loading…</p> : <textarea value={text} onChange={(e) => setText(e.target.value)} spellCheck={false} />}
        {msg && <div className={msg.kind === "error" ? "error" : "ok-msg"}>{msg.text}</div>}
        <div className="row"><button type="button" onClick={onClose}>Close</button><button type="button" onClick={() => { if (confirm("Put the built-in phase list back?")) void save(true); }}>Reset to built-in</button><button type="button" className="primary" onClick={() => save()}>Save</button></div>
      </div>
    </div>
  );
}
